import React, { useState, useRef, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import AddExperienceModal from '../components/AddExperienceModal';
import ConfirmDeleteModal from '../../../organisms/ConfirmDeleteModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/experience-tab.css';

interface VolunteerExperienceData {
  uid?: string;
  employer: string;
  jobTitle: string;
  space: string;
  role: string;
  startDate: string;
  endDate: string;
  location: string;
  isCurrentPosition: boolean;
  skills: string[];
  description: string;
  achievements: string[];
}

interface VolunteerExperience {
  uid: string;
  organization_name: string;
  position: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

interface UserData {
  volunteer_experience: VolunteerExperience[] | null;
}

interface VolunteerExperienceTabProps {
  userData: UserData | null;
  experiences: VolunteerExperience[];
}

interface ExperienceCardProps {
  experience: VolunteerExperience;
  onEdit: (experience: VolunteerExperience) => void;
  onDelete: (uid: string) => void;
}

function ExperienceCard({ experience, onEdit, onDelete }: ExperienceCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatDateRange = (): string => {
    const startDate = formatDate(experience.start_date);
    const endDate = experience.is_current ? 'Present' : formatDate(experience.end_date);

    if (startDate && !endDate) {
      return `from ${startDate}`;
    }
    if (!startDate && endDate) {
      return `upto ${endDate}`;
    }
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    }
    return '';
  };

  const dateRange = formatDateRange();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="experience-card">
      <div className="experience-card-header">
        <div className="experience-card-logo">
          <span className="experience-card-logo-icon">
            <Building2 size={16} />
          </span>
        </div>
        <div className="experience-card-info">
          <h3 className="experience-card-title">{experience.organization_name}</h3>
          <p className="experience-card-subtitle">{experience.position}</p>
        </div>
        {experience.location && (
          <p className="experience-card-location">{experience.location}</p>
        )}
        <p className="experience-card-date">{dateRange}</p>
        <div className="experience-card-menu-container" ref={menuRef}>
          <button 
            className="experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="experience-card-dropdown">
              <button
                className="experience-card-dropdown-item"
                onClick={() => {
                  onEdit(experience);
                  setMenuOpen(false);
                }}
              >
                Edit experience
              </button>
              <button
                className="experience-card-dropdown-item experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(experience.uid);
                  setMenuOpen(false);
                }}
              >
                Delete experience
              </button>
            </div>
          )}
        </div>
      </div>

      {(experience.description || (experience.achievements && experience.achievements.length > 0)) && (
        <div className="experience-card-body">
          {experience.description && (
            <p className="experience-card-description">{experience.description}</p>
          )}
          {experience.achievements && experience.achievements.length > 0 && (
            <ul className="experience-card-achievements">
              {experience.achievements.map((achievement, index) => (
                <li key={index}>{achievement}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {experience.skills && experience.skills.length > 0 && (
        <div className="experience-card-skills">
          <span className="experience-card-skills-label">Skills:</span>
          <div className="experience-card-skills-list">
            {experience.skills.map((skill, index) => (
              <span key={index} className="experience-card-skill-chip">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VolunteerExperienceTab({ userData, experiences }: VolunteerExperienceTabProps) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [volunteerExperiences, setVolunteerExperiences] = useState<VolunteerExperience[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<VolunteerExperience | null>(null);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  useEffect(() => {
    if (experiences && experiences.length > 0) {
      setVolunteerExperiences(experiences);
    }
  }, [experiences]);

  const handleImport = () => {
  };

  const handleAdd = () => {
    setSelectedExperience(null);
    setAddModalOpen(true);
  };

  const handleEdit = (experience: VolunteerExperience) => {
    setSelectedExperience(experience);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (uid: string) => {
    setDeleteUid(uid);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUid) return;

    setIsDeleting(true);

    try {
      
      await api.delete(API_ENDPOINTS.users.deleteVolunteerExperience(deleteUid));
      
      setVolunteerExperiences(volunteerExperiences.filter(exp => exp.uid !== deleteUid));
      setToast({ message: 'Volunteer experience deleted successfully! ✓', type: 'success' });
      setDeleteModalOpen(false);
      setDeleteUid(null);
    } catch (error) {
      setToast({ message: 'Failed to delete volunteer experience. Please try again.', type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddExperience = async (data: VolunteerExperienceData) => {
    setIsCreating(true);

    try {
      const payload: any = {
        organization_name: data.employer,
        position: data.jobTitle,
        is_current: data.isCurrentPosition,
      };

      if (data.uid) {
        payload.uid = data.uid;
      }

      if (data.skills.length > 0) payload.skills = data.skills;
      if (data.description.trim()) payload.description = data.description.trim();
      if (data.achievements.length > 0) payload.achievements = data.achievements;
      if (data.startDate) payload.start_date = data.startDate;
      if (data.endDate) payload.end_date = data.endDate;
      if (data.location.trim()) payload.location = data.location.trim();

      const isEditMode = !!data.uid;

      const response = await api.post(API_ENDPOINTS.users.updateVolunteerExperience, payload);
      

      if (isEditMode) {
        setVolunteerExperiences(volunteerExperiences.map(exp => 
          exp.uid === data.uid ? { ...exp, ...response } : exp
        ));
        setToast({ message: 'Volunteer experience updated successfully! ✓', type: 'success' });
        setEditModalOpen(false);
        setSelectedExperience(null);
      } else {
        if (response && response.uid) {
          setVolunteerExperiences([...volunteerExperiences, response]);
        }
        setToast({ message: 'Volunteer experience added successfully! ✓', type: 'success' });
        setAddModalOpen(false);
      }
    } catch (error) {
      setToast({ message: 'Failed to save volunteer experience. Please try again.', type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const experienceToModalData = (exp: VolunteerExperience | null): any => {
    if (!exp) return null;
    return {
      uid: exp.uid,
      employer_name: exp.organization_name,
      job_title: exp.position,
      skills: exp.skills,
      description: exp.description,
      achievements: exp.achievements,
      start_date: exp.start_date,
      end_date: exp.end_date,
      location: exp.location,
      is_current: exp.is_current,
    };
  };

  return (
    <>
      <div className="experience-tab">
        <div className="experience-tab-header">
          <h1 className="experience-tab-title">Volunteer Experience</h1>
          <button className="experience-tab-btn experience-tab-btn--import" onClick={handleImport} type="button">
            <span className="experience-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="experience-tab-content">
          <button 
            type="button" 
            className="experience-tab-add-btn"
            onClick={handleAdd}
          >
            <span className="experience-tab-add-icon">+</span>
          </button>

          {volunteerExperiences.length > 0 && (
            <div className="experience-list">
              {volunteerExperiences.map((exp) => (
                <ExperienceCard 
                  key={exp.uid} 
                  experience={exp}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddExperienceModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddExperience}
        title="Add Volunteer Experience"
        employerLabel="Organization"
        jobTitleLabel="Position"
      />

      <AddExperienceModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedExperience(null);
        }}
        onAdd={handleAddExperience}
        title="Edit Volunteer Experience"
        employerLabel="Organization"
        jobTitleLabel="Position"
        initialData={experienceToModalData(selectedExperience)}
        isEditMode={true}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={volunteerExperiences.find(exp => exp.uid === deleteUid)?.organization_name || 'this volunteer experience'}
        itemType="Volunteer Experience"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteUid(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
